import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { candidaturaService } from '@/services/candidaturaService'
import { tenantService } from '@/services/tenantService'
import { CandidaturaCadastro } from '@/types/candidatura'
import { TenantAdmin } from '@/types/tenant'

/**
 * Validação do cadastro e liberação do acesso (PDF do Consultor, Etapa 3).
 *
 * <p>O botão de aprovar exige tenant porque a regra da Etapa 3 pede "perfil de acesso
 * corretamente configurado" — e consultor sem tenant não enxerga nada, já que o escopo
 * de leitura é fail-closed. Aprovar sem isso seria aprovar pela metade.
 */

interface Props {
  candidaturaId: string | null
  onFechar: () => void
  onAprovado: () => void
}

const campo = (rotulo: string, valor?: string | null) =>
  valor ? (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
      <Typography variant="body2">{valor}</Typography>
    </Grid>
  ) : null

export const AnalisarCadastroDialog: FC<Props> = ({ candidaturaId, onFechar, onAprovado }) => {
  const { enqueueSnackbar } = useSnackbar()

  const [cadastro, setCadastro] = useState<CandidaturaCadastro | null>(null)
  const [tenants, setTenants] = useState<TenantAdmin[]>([])
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const [tenantId, setTenantId] = useState('')
  const [tipoCanal, setTipoCanal] = useState('')
  const [parecer, setParecer] = useState('')
  const [aprovando, setAprovando] = useState(false)

  useEffect(() => {
    if (!candidaturaId) {
      setCadastro(null)
      return
    }
    let vivo = true
    setCarregando(true)
    setErro(null)
    setTenantId('')
    setTipoCanal('')
    setParecer('')
    Promise.all([
      candidaturaService.obterCadastro(candidaturaId),
      tenantService.listar().catch(() => [] as TenantAdmin[]),
    ])
      .then(([c, ts]) => {
        if (!vivo) return
        setCadastro(c)
        setTenants((ts as TenantAdmin[]).filter((t) => t.ativo))
        setTenantId(c.tenantId ?? '')
      })
      .catch((e) => {
        if (vivo) setErro((e as { message?: string })?.message ?? 'Falha ao carregar o cadastro')
      })
      .finally(() => {
        if (vivo) setCarregando(false)
      })
    return () => {
      vivo = false
    }
  }, [candidaturaId])

  const emAnalise = cadastro?.status === 'CADASTRO_EM_ANALISE'
  const podeAprovar = emAnalise && !!tenantId && !!cadastro?.aceiteTermosEm && cadastro.possuiUsuario

  const aprovar = async () => {
    if (!candidaturaId || !podeAprovar) return
    setAprovando(true)
    try {
      await candidaturaService.aprovarCadastro(candidaturaId, {
        tenantId,
        tipoCanal: tipoCanal || undefined,
        parecer: parecer.trim() || undefined,
      })
      enqueueSnackbar('Cadastro aprovado e acesso configurado', { variant: 'success' })
      onAprovado()
      onFechar()
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Falha ao aprovar o cadastro',
        { variant: 'error' }
      )
    } finally {
      setAprovando(false)
    }
  }

  return (
    <Dialog open={!!candidaturaId} onClose={onFechar} maxWidth="md" fullWidth>
      <DialogTitle>Validar cadastro</DialogTitle>
      <DialogContent dividers>
        {carregando && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {erro && <Alert severity="error">{erro}</Alert>}

        {cadastro && !carregando && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{cadastro.nome}</Typography>
              <Typography variant="caption" color="text.secondary">
                {cadastro.email} · {cadastro.telefone}
              </Typography>
            </Box>

            {/* As duas primeiras das quatro condições da Etapa 3, visíveis antes de aprovar. */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={cadastro.possuiUsuario ? 'Cadastro concluído' : 'Cadastro não concluído'}
                color={cadastro.possuiUsuario ? 'success' : 'default'}
              />
              <Chip
                size="small"
                label={
                  cadastro.aceiteTermosEm
                    ? `Termos aceitos${cadastro.aceiteTermosVersao ? ` (${cadastro.aceiteTermosVersao})` : ''}`
                    : 'Termos não aceitos'
                }
                color={cadastro.aceiteTermosEm ? 'success' : 'warning'}
              />
            </Stack>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Perfil profissional</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Grid container spacing={2}>
                {campo('Formação', cadastro.formacao)}
                {campo('Região de atuação', cadastro.regiaoAtuacao)}
                {campo('Segmentos de experiência', cadastro.segmentosExperiencia)}
                {campo('Mercado de origem', cadastro.mercadoOrigem)}
                {campo('Tipos de empresa atendidos', cadastro.tiposEmpresaAtendidos)}
                {campo('Produtos já comercializados', cadastro.produtosComercializados)}
                {campo('Principal relacionamento', cadastro.principalRelacionamento)}
              </Grid>
              {cadastro.resumoProfissional && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {cadastro.resumoProfissional}
                  </Typography>
                </Paper>
              )}
            </Box>

            {cadastro.experiencias.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Empresas em que trabalhou</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={1.5}>
                  {cadastro.experiencias.map((e, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {e.cargo} — {e.empresa}
                      </Typography>
                      {e.periodo && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {e.periodo}{e.segmentos ? ` · ${e.segmentos}` : ''}
                        </Typography>
                      )}
                      {e.atividades && (
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                          {e.atividades}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {emAnalise ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Liberação do acesso</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={2}>
                  <TextField
                    select
                    label="Tenant"
                    required
                    size="small"
                    fullWidth
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    helperText="Sem tenant o consultor não enxerga dado nenhum."
                  >
                    {tenants.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Canal"
                    size="small"
                    fullWidth
                    value={tipoCanal}
                    onChange={(e) => setTipoCanal(e.target.value)}
                  >
                    <MenuItem value="DIRETO">Direto</MenuItem>
                    <MenuItem value="PARCEIRO">Parceiro</MenuItem>
                    <MenuItem value="INDICACAO">Indicação</MenuItem>
                  </TextField>
                  <TextField
                    label="Parecer"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={parecer}
                    onChange={(e) => setParecer(e.target.value)}
                  />
                  <Alert severity="info">
                    Aprovar configura o acesso, mas ainda não libera a criação de reservas:
                    isso depende do primeiro acesso do consultor.
                  </Alert>
                </Stack>
              </Box>
            ) : (
              <Alert severity="info">
                Este cadastro não está em análise (status atual: {cadastro.status}).
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Fechar</Button>
        {emAnalise && (
          <Button variant="contained" onClick={aprovar} disabled={!podeAprovar || aprovando}>
            Aprovar e liberar acesso
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
